import FieldGenerator from "@/components/FormGenerator/FieldGenerator"
import {
  Box,
  Typography,
  TableContainer as MUITableContainer,
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
  Stack,
  Grid,
  Button,
  FormGroup,
  FormControlLabel,
  Checkbox,
  TableProps,
} from "@mui/material"
import { FieldTypes, IField, IFieldContext, RHFField } from "../../../interface/FormGenerator"
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react"
import useSpeedTest, { SOMResponse } from "@/hooks/useSpeedTest"
import Loader from "@/components/Loader/Loader"
import dayjs from "dayjs"
import { COLORS } from "@/theme/localTheme"
import FieldContext from "@/components/FormGenerator/FieldContext"
import { SectionField, SubmissionErrorSectionDetails } from "./types"
import ModalPopup from "@/components/ModalComponents/ModalPopup"
import { Error } from "@mui/icons-material"
import { Controller, useFormContext, get } from "react-hook-form"
import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import { isObjectEmpty, isStringParsable } from "@/utils/CommonFunctions"
import { validateRegex } from "@/components/FormGenerator/FormFields/CJS"
import { ErrorSX } from "@/components/FormGenerator/Styles/CommonStyles.sx"
import Image from "next/image"
import ImagePreview from "@/components/layout-components/ImagePreview"
import { TImageModal } from "../../../interface/ImagePreview"
import { UPLOAD_IMAGE_SIZE } from "@/utils/constants/globalVariables"

export default function ExtendedFieldGenerator({ fields, setField }: { fields: Array<RHFField>; setField?: (key: string, value: unknown) => void }) {
  const [previewImage, setPreviewImage] = useState<TImageModal>({ isOpen: false, images: [], startIndex: 0 })

  const handleOpenImagePreview = (images: string[], index: number) => {
    setPreviewImage({
      isOpen: true,
      images,
      startIndex: index,
    })
  }

  const handleCloseImagePreview = () => {
    setPreviewImage({
      isOpen: false,
      images: [],
      startIndex: 0,
    })
  }

  return (
    <Box style={{ display: "flex", gap: 32, justifyContent: "space-between", flexWrap: "wrap" }} fontSize={"18px"}>
      {fields.map((field, i) => {
        const shouldHaveFullWidth =
          field.field_type === "textarea" || field.field_type === "radio" || field.field_type === "signature" || field.field_type === "image"
        return !field.field_type ? null : !field.keyword ? null : field.field_type === "table_url" && Boolean(field.answer_value) ? (
          <Box sx={{ width: "100%" }} key={`${field.id}_${i}`}>
            <FieldContext.Provider value={{ formKey: field.keyword, form: { ...field } }}>
              <TableContainer disabled={!field.can_edit} handleChange={(value: unknown) => setField?.(field.keyword, value)} />
            </FieldContext.Provider>
          </Box>
        ) : field.field_type === "speed_test" ? (
          <FieldContext.Provider key={`${field.id}_${i}`} value={{ formKey: field.keyword, form: { ...field } }}>
            <Box sx={{ width: "100%" }}>
              <SpeedTest
                label={field?.label || ""}
                results={typeof field.answer_value === "string" && field.answer_value.length ? JSON.parse(field.answer_value) : null}
                handleChange={(data: unknown) => setField?.(field.keyword, JSON.stringify(data))}
                disabled={!field.can_edit}
              />
            </Box>
          </FieldContext.Provider>
        ) : field.field_type === "JSON" ? null : (
          <Stack key={`${field.id}_${i}`} sx={{ position: "relative", width: shouldHaveFullWidth ? "100%" : "48%" }}>
            <FieldGenerator formKey={field.keyword} field={field} />
            {field?.images?.length ? (
              <Box display="flex" gap={"12px"}>
                {field.images.map((image, i) => {
                  return (
                    <Box key={image} sx={{ cursor: "pointer" }} onClick={() => handleOpenImagePreview(field.images || [], i)}>
                      <Image src={image} width={52} height={52} style={{ borderRadius: 6 }} alt={`property_specific_${image}`} />
                    </Box>
                  )
                })}
              </Box>
            ) : null}
          </Stack>
        )
      })}
      <ImagePreview
        startIndex={previewImage.startIndex}
        images={previewImage.images}
        isOpen={previewImage.isOpen}
        onClose={handleCloseImagePreview}
      />
    </Box>
  )
}

interface Option {
  alias?: string
  label: string
  value: string
  is_default?: boolean
}

interface TableField {
  options: Option[]
  field_type: FieldTypes
  answer_value: string
  disabled?: boolean
}

interface TableRowData {
  [key: string]: string | TableField
}

type TableData = TableRowData[]

export function TableContainer(props?: Partial<IFieldContext & TableProps & { disabled?: boolean; handleChange?: (data: unknown) => void }>) {
  const { control, formState } = useFormContext()
  const { errors } = formState
  const contextData = useContext(FieldContext)

  const { formKey, form } = Object.keys(contextData).length > 0 ? contextData : (props as IFieldContext)

  const [tableData, setTableData] = useState<TableData>(() => {
    if (typeof form.answer_value === "string" && isStringParsable(form.answer_value)) {
      const table = JSON.parse(form.answer_value) as TableData
      const updatedTable: TableData = table
      table.forEach((row, index) => {
        Object.keys(row).forEach((cellKey) => {
          const cellValue = row[cellKey]
          if (typeof cellValue === "object") {
            const defaultOption = cellValue.options.find((option) => option?.is_default)
            const responseField = {
              options: cellValue.options,
              answer_value: cellValue.answer_value ? cellValue.answer_value : defaultOption?.value ? defaultOption.value : "",
              field_type: cellValue.field_type,
            }
            if (updatedTable[index]?.[cellKey]) {
              updatedTable[index] = { ...updatedTable[index], [cellKey]: responseField }
            }
          }
        })
      })
      return updatedTable
    }
    return []
  })

  const handleFieldChange = useCallback(
    (columnKey: string, fieldValue: string, rowIndex: number) => {
      setTableData((prev) => {
        let values = prev
        const prevValues: TableField = values[rowIndex]?.[columnKey] as TableField
        values[rowIndex] = { ...values[rowIndex], [columnKey]: { ...prevValues, answer_value: fieldValue } }
        const finalString = JSON.stringify(values)
        props?.handleChange?.(finalString)
        return values
      })
    },
    [props],
  )

  if (!form || !formKey) return null

  const error = get(errors, formKey)

  return (
    <>
      <Controller
        name={formKey}
        defaultValue={form?.answer_value ?? ""}
        control={control}
        rules={{
          required: typeof form.required === "boolean" && form.required ? "Required" : form.required,
          validate: (value) => {
            if (!form.required && !value) return undefined
            if (form.regex) {
              return validateRegex(value, form.regex)
            } else return undefined
          },
        }}
        render={() => (
          <MUITableContainer sx={{ display: "flex", justifyContent: "center", alignItems: "center", width: "100%" }}>
            <Table sx={{ width: "fit-content", maxWidth: "85%" }}>
              <TableHead
                sx={{
                  backgroundColor: "#2B4D1D",
                }}
              >
                <TableRow>
                  {tableData && tableData[0] && !isObjectEmpty(tableData[0]) ? Object.keys(tableData[0]).filter((column) => !column?.toLowerCase()?.includes('hide_')).map((columnHeader, index) => (
                    <TableCell
                      key={index}
                      sx={{
                        textTransform: "capitalize",
                      }}
                    >
                      {columnHeader}
                    </TableCell>
                  )) : null}
                </TableRow>
              </TableHead>
              <TableBody>
                {tableData.map((rowObj, rowIndex) => {
                  return (
                    <TableRow key={rowIndex}>
                      {Object.keys(rowObj).filter((column) => !column?.toLowerCase()?.includes('hide_')).map((columnKey, index) => {
                        const cellValue = rowObj[columnKey]
                        if (typeof cellValue === "object") {
                          const responseField: RHFField = {
                            id: `${index}_${rowIndex}_keyword`,
                            keyword: `${index}_${rowIndex}_keyword`,
                            can_edit: form.disabled_cell_keys?.length ? !form.disabled_cell_keys?.includes(columnKey) : !props?.disabled,
                            options: cellValue.options,
                            answer_value: cellValue.answer_value,
                            field_type: cellValue.field_type,
                          }
                          return (
                            <TableCell key={index} sx={{ width: "30%", minWidth: "240px" }}>
                              <FieldGenerator
                                formKey={responseField.keyword}
                                onChange={(value: unknown) => {
                                  handleFieldChange(columnKey, value as string, rowIndex)
                                }}
                                field={responseField}
                              />
                            </TableCell>
                          )
                        } else {
                          return (
                            <TableCell key={index} width={200}>
                              {String(cellValue)}
                            </TableCell>
                          )
                        }
                      })}
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </MUITableContainer>
        )}
      />
      {((typeof form.required === "string" && form.required) || form.regex) && error?.message && (
        <Box className="ErrorVai" sx={ErrorSX}>
          <Typography fontSize={"0.75rem"}>{error?.message}</Typography>
        </Box>
      )}
    </>
  )
}

function SpeedTest({
  label,
  results,
  handleChange,
  disabled,
}: {
  label: string
  results?: SOMResponse | null
  handleChange?: (data: unknown) => void
  disabled: boolean
}) {
  const { isLoading, result, startTest } = useSpeedTest()

  useEffect(() => {
    if (result?.download && !isLoading) {
      handleChange?.(result)
    }
  }, [result?.download, isLoading, result, handleChange])

  const finalResults = useMemo(() => {
    return result?.download ? result : results?.download ? results : null
  }, [result, results])

  return (
    <Stack gap="30px">
      <Box
        component="fieldset"
        sx={{
          padding: "1rem",
          border: "1px solid rgba(255, 255, 255, 0.7)",
          borderRadius: "6px",
          color: "rgba(255, 255, 255, 0.7)",
          minHeight: "120px",
        }}
      >
        <legend
          style={{
            fontFamily: "Roboto, Helvetica, Arial, sans-serif",
            fontWeight: "400",
            fontSize: "0.75rem",
            paddingLeft: "4px",
            paddingRight: "4px",
          }}
        >
          {label}
        </legend>
        <Box>
          {!isLoading && finalResults?.download ? (
            <Grid container>
              <Grid item xs={4}>
                <Box display="flex" flexDirection="column">
                  <Box display="flex" mb={"0.25em"}>
                    <Typography sx={{ fontSize: "14px", width: "5rem" }}>Download:</Typography>
                    <Typography sx={{ fontSize: "14px", color: "#fff" }}>{finalResults?.download ? finalResults?.download : "00.00"} Mbps</Typography>
                  </Box>
                  <Box display="flex" mb={"0.25em"}>
                    <Typography sx={{ fontSize: "14px", width: "5rem" }}>Upload:</Typography>
                    <Typography sx={{ fontSize: "14px", color: "#fff" }}>{finalResults?.upload ? finalResults?.upload : "00.00"} Mbps</Typography>
                  </Box>
                  <Box display="flex" mb={"0.25em"}>
                    <Typography sx={{ fontSize: "14px", width: "5rem" }}>Latency:</Typography>
                    <Typography sx={{ fontSize: "14px", color: "#fff" }}>{finalResults?.latency ? finalResults?.latency : "00"} ms</Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={4}>
                <Box display="flex" flexDirection="column">
                  <Box display="flex" gap="12px" mb={"0.25em"}>
                    <Typography sx={{ fontSize: "14px", width: "5rem" }}>Jitter:</Typography>
                    <Typography sx={{ fontSize: "14px", color: "#fff" }}>{finalResults?.jitter ? finalResults?.jitter : "00"} ms</Typography>
                  </Box>
                  <Box display="flex" gap="12px" mb={"0.25em"}>
                    <Typography sx={{ fontSize: "14px", width: "5rem" }}>Test Server:</Typography>
                    <Typography sx={{ fontSize: "14px", color: "#fff" }}>
                      {finalResults?.testServer ? finalResults?.testServer : "Unknown"}
                    </Typography>
                  </Box>
                  <Box display="flex" gap="12px" mb={"0.25em"}>
                    <Typography sx={{ fontSize: "14px", width: "5rem" }}>IP:</Typography>
                    <Typography sx={{ fontSize: "14px", color: "#fff" }}>
                      {finalResults?.ip_address ? finalResults?.ip_address : "Unknown"}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={4}>
                <Box display="flex" gap="12px" mb={"0.25em"}>
                  <Typography sx={{ fontSize: "14px", width: "5rem" }}>HostName:</Typography>
                  <Typography sx={{ fontSize: "14px", color: "#fff" }}>{finalResults?.hostname ? finalResults?.hostname : "Unknown"}</Typography>
                </Box>
                <Box display="flex" gap="12px" mb={"0.25em"}>
                  <Typography sx={{ fontSize: "14px", width: "5rem" }}>Timestamp:</Typography>
                  <Typography sx={{ fontSize: "14px", color: "#fff" }}>
                    {dayjs(finalResults?.testDate || new Date()).format("DD-MM-YYYY HH:MM:ss")}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          ) : isLoading ? (
            <Stack sx={{ minHeight: "104px", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Loader loading type="simple" size={30} />
              <Typography fontSize="14px" color="#3ACF01">{`Please wait till we fetch the SPEED TEST RESULTS`}</Typography>
            </Stack>
          ) : (
            <Box sx={{ minHeight: "104px", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Typography fontSize="14px" fontStyle="italic">{`**Speed test result will be shown here`}</Typography>
            </Box>
          )}
        </Box>
      </Box>
      <Box style={{ height: "1px", backgroundColor: COLORS.divider }} />
      <Box display="flex" justifyContent="center">
        <Button disabled={disabled || isLoading} variant="contained" onClick={startTest}>
          Run Speed Test
        </Button>
      </Box>
    </Stack>
  )
}

export type GetFieldsParams = {
  fields: SectionField[]
  viewOnly?: boolean
  wifi?: string
  wifi_pass?: string
  ticketId?: number
  formType?: "arrival" | "departure"
  isAdminOrManager?: boolean
}

export function getFields({ fields, viewOnly = false, wifi, wifi_pass, ticketId, formType, isAdminOrManager }: GetFieldsParams): RHFField[] {
  const fieldsArray: RHFField[] = []
  fields.forEach((field) => {
    if (
      field.field_type === "JSON" &&
      typeof field.answer_value === "string" &&
      field.keyword === "property_specific_details" &&
      isStringParsable(field.answer_value)
    ) {
      const jsonFields: SectionField[] = JSON.parse(field.answer_value)
      if (Array.isArray(jsonFields)) {
        jsonFields.forEach((jsonField, i) => {
          fieldsArray.push({
            id: `${i}_JSON`,
            keyword: `${jsonField.keyword}_JSON`,
            label: jsonField.label,
            secondary_label: jsonField.field_type === "image" ? `You can upload up to ${jsonField.max_length ?? 1} photos` : undefined,
            answer_value:
              jsonField.field_type === "date"
                ? !isNaN(Number(jsonField.answer_value))
                  ? Number(jsonField.answer_value)
                  : jsonField.answer_value
                : jsonField.answer_value,
            field_type: jsonField.field_type,
            images: jsonField?.images || [],
            can_edit: viewOnly ? false : jsonField.can_edit,
            required: viewOnly ? false : jsonField.is_required && jsonField.can_edit ? `${jsonField.label} is required!` : false,
            options: jsonField.options,
            max_length: jsonField.max_length,
            max_size: jsonField.max_size ? jsonField.max_size : jsonField.field_type === "image" ? UPLOAD_IMAGE_SIZE : undefined,
            regex:
              jsonField.regex?.length && jsonField.can_edit
                ? [{ regex: new RegExp(jsonField.regex), message: "Please enter valid data!" }]
                : undefined,
            url_options:
              jsonField.field_type === "dropdown_url" && jsonField.options?.length
                ? {
                    url: jsonField.options[0].value,
                    queryParams: jsonField.keyword === "prepared_by_guest_arrival" ? `ticket_id=${ticketId}` : undefined,
                  }
                : undefined,
            disabled_cell_keys: !isAdminOrManager || formType === "departure" ? ["Configurations"] : undefined,
          })
        })
      }
    } else {
      fieldsArray.push({
        id: (field.fields_table_id || field.id).toString(),
        keyword: field.keyword,
        label: field.label,
        secondary_label: field.field_type === "image" ? `You can upload upto ${field.max_length ?? 1} photos` : undefined,
        answer_value:
          field.keyword === "password_wifi_guest_arrival"
            ? wifi
            : field.keyword === "confirm_UP_guest_arrival"
              ? wifi_pass
              : field.field_type === "date"
                ? !isNaN(Number(field.answer_value))
                  ? Number(field.answer_value)
                  : field.answer_value
                : field.answer_value,
        field_type: field.field_type,
        can_edit: viewOnly ? false : field.can_edit,
        required: viewOnly ? false : field.is_required && field.can_edit ? `${field.label} is required!` : false,
        options: field.options,
        max_length: field.max_length,
        max_size: field.max_size ? field.max_size : field.field_type === "image" ? UPLOAD_IMAGE_SIZE : undefined,
        regex: field.regex?.length && field.can_edit ? [{ regex: new RegExp(field.regex), message: "Please enter valid data!" }] : undefined,
        url_options:
          field.field_type === "dropdown_url" && field.options?.length
            ? {
                url: field.options[0].value,
                queryParams:
                  field.keyword === "prepared_by_guest_arrival" || field.keyword === "report_completed_guest_depature"
                    ? `ticket_id=${ticketId}`
                    : undefined,
              }
            : undefined,
        disabled_cell_keys: !isAdminOrManager || formType === "departure" ? ["Configurations"] : undefined,
      })
    }
  })
  return fieldsArray
}

export function checkIfValuesAreSimilar(value1: unknown, value2: unknown): boolean {
  if (value1 == undefined || value2 == undefined) {
    return true
  } else if (Array.isArray(value1) || Array.isArray(value2)) {
    if (Array.isArray(value1) && Array.isArray(value2)) {
      return JSON.stringify(value1) === JSON.stringify(value2)
    }
    let arrayStringified = ""
    let alreadyStringified = typeof value1 === "string" ? value1 : value2
    if (typeof value1 !== "string") {
      arrayStringified = JSON.stringify(value1)
    } else {
      arrayStringified = JSON.stringify(value2)
    }
    return arrayStringified === alreadyStringified
  } else if (typeof value1 === "object" && typeof value2 === "object") {
    return JSON.stringify(value1) === JSON.stringify(value2)
  } else {
    return value1 == value2
  }
}

function formatErrorForSubmission(errorArray: SubmissionErrorSectionDetails[]): {
  [key: number]: SubmissionErrorSectionDetails[]
} {
  return errorArray.reduce<{ [key: number]: SubmissionErrorSectionDetails[] }>((acc, item) => {
    const { page } = item
    if (!acc[page]) {
      acc[page] = []
    }
    acc[page].push(item)
    return acc
  }, {})
}

export function SubmissionErrorModal({
  isOpen,
  errors,
  toggle,
  onFillRequired,
}: {
  isOpen: boolean
  toggle: (value: boolean) => void
  errors: SubmissionErrorSectionDetails[]
  onFillRequired?: (step: number) => void
}) {
  const formattedErrors = useMemo(() => {
    return formatErrorForSubmission(errors)
  }, [errors])
  return (
    <ModalPopup trigger={isOpen} setTriggerFnc={() => toggle(!isOpen)} maxWidths="lg">
      <Box sx={{ width: "26.25rem", textAlign: "center", p: "2%" }}>
        <Typography
          display={"flex"}
          alignItems={"center"}
          justifyContent={"flex-start"}
          paddingBottom={"1rem"}
          borderBottom={`1px solid ${COLORS.divider}`}
          fontWeight={500}
          color={COLORS.adminAppPrimaryBrandColor}
          marginBottom={"1rem"}
        >
          <Error fontSize="small" /> Error : Missing Mandatory Fields
        </Typography>
        <Typography textAlign={"left"} fontSize={"14px"} color={COLORS.textWhite} marginBottom={"1rem"}>
          Some required fields are missing. Please complete the following sections.
        </Typography>
        <Box
          borderRadius={"6px"}
          marginBottom={"1rem"}
          sx={{ backgroundColor: COLORS.surfaceBlack }}
          maxHeight={"24rem"}
          padding={"0.5rem"}
          overflow={"scroll"}
        >
          {errors?.length
            ? Object.entries(formattedErrors).map(([key, items]) => (
                <Box
                  key={key}
                  display={"flex"}
                  flexDirection={"column"}
                  justifyContent={"center"}
                  alignItems={"flex-start"}
                  padding={"0.5rem"}
                  borderBottom={`1px solid ${COLORS.divider}`}
                >
                  <Typography paddingBottom={"0.25rem"} fontSize={"14px"}>
                    Step {key} :
                  </Typography>
                  {items.map((item) => (
                    <Typography key={item.id} fontSize={"14px"}>
                      {" "}
                      - {item.label}
                    </Typography>
                  ))}
                </Box>
              ))
            : null}
        </Box>
        <Box display={"flex"} alignItems={"center"} justifyContent={"space-between"} gap={2}>
          <Button
            component="label"
            variant="contained"
            sx={{
              color: "#fff",
              border: `1px solid ${COLORS.adminAppPrimaryBrandColor}`,
              backgroundColor: "transparent",
              "&:hover": {
                backgroundColor: `${COLORS.secondaryButton}`,
              },
            }}
            onClick={() => toggle(false)}
          >
            Cancel
          </Button>
          <Button
            component="label"
            variant="contained"
            sx={{
              color: "#fff",
              backgroundColor: `${COLORS.primaryButton}`,
              "&:hover": {
                backgroundColor: `${COLORS.primaryButton}`,
              },
            }}
            onClick={() => {
              const step = errors[0].page
              onFillRequired?.(step ?? 1)
            }}
          >
            Fill Required Fields
          </Button>
        </Box>
      </Box>
    </ModalPopup>
  )
}

export function VerificationSection({
  fields,
  sectionLabel,
  isReviewed,
  handleVerification,
  ...props
}: {
  label?: string
  loading?: boolean
  sectionLabel?: string
  isReviewed: boolean
  handleVerification: (value: boolean) => void
  fields: SectionField[]
}) {
  const { control, setValue } = useFormContext()
  const contextData = useContext(FieldContext)

  const { formKey, form, onChange: onValueChange, ...rest } = Object.keys(contextData).length > 0 ? contextData : (props as IFieldContext)
  const checkboxField = useMemo(() => {
    return fields.find((field) => field.field_type === "checkbox")
  }, [fields])

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const status = event.currentTarget.checked
    handleVerification(status)
    setValue(formKey, status)
  }

  return (
    <Controller
      name={formKey}
      control={control}
      disabled={isReviewed}
      render={() => (
        <Stack paddingY="30px" paddingX="38px" sx={{ background: "#343E30", borderRadius: "12px" }}>
          <Box sx={{ borderBottom: `1px solid #FFFFFF33`, paddingBottom: "24px" }}>
            <Typography fontSize="24px" fontWeight="500">
              {sectionLabel}
            </Typography>
          </Box>
          <Stack gap="30px">
            {checkboxField?.id && (
              <Box display="flex" gap="12px">
                <FormGroup sx={{ mt: 2 }}>
                  <FormControlLabel
                    sx={{ alignItems: "start" }}
                    disabled={isReviewed}
                    control={
                      <Checkbox
                        id={checkboxField.keyword}
                        sx={{ color: "white", mt: -1 }}
                        defaultChecked={isReviewed}
                        onChange={handleCheckboxChange}
                      />
                    }
                    label={checkboxField.label}
                  />
                </FormGroup>
              </Box>
            )}
          </Stack>
        </Stack>
      )}
    />
  )
}

export function FooterButtons({
  loading,
  viewOnly,
  activePage,
  totalPages,
  role,
  isReviewed,
  enableConfirmVerification,
  submitHandler,
  onPreviousHandler,
}: {
  viewOnly?: boolean
  activePage: number
  totalPages: number
  loading?: boolean
  enableConfirmVerification: boolean
  isReviewed: boolean
  onPreviousHandler?: () => void
  submitHandler?: () => void
  role: "admin" | "employee" | "manager"
}) {
  const submitBtnLabel: string = useMemo(() => {
    if (activePage < totalPages) {
      return viewOnly ? "Next" : "Save & Continue"
    } else if (activePage === totalPages) {
      if (role === "employee") return viewOnly ? "Exit" : "Save & Submit"
      return isReviewed ? "Exit" : "Confirm verification & submit"
    }
    return ""
  }, [activePage, totalPages, viewOnly, role, isReviewed])

  const disabled = useMemo(() => {
    if (role === "employee" && loading) {
      return true
    } else if ((role === "admin" || role === "manager") && activePage === totalPages) {
      return !isReviewed && (!enableConfirmVerification || loading)
    } else return loading
  }, [role, loading, activePage, totalPages, isReviewed, enableConfirmVerification])

  return (
    <Box style={{ display: "flex", marginTop: "40px", justifyContent: "space-between" }}>
      <Button variant="outlined" sx={{ color: "white" }} disabled={loading} onClick={onPreviousHandler}>
        {activePage > 1 && <ArrowBackIcon fontSize="small" sx={{ marginRight: "8px" }} />}
        {activePage > 1 ? "PREVIOUS" : "CANCEL"}
      </Button>
      <Button type="submit" variant="contained" disabled={disabled} onClick={submitHandler}>
        {submitBtnLabel}
      </Button>
    </Box>
  )
}

export function CancelConfirmInModal({
  isOpen,
  toggle,
  onConfirm,
  onCancel,
}: {
  isOpen: boolean
  toggle: (value: boolean) => void
  onConfirm: () => void
  onCancel: () => void
}) {
  return (
    <ModalPopup trigger={isOpen} setTriggerFnc={() => toggle(!isOpen)}>
      <Box sx={{ width: "26.25rem", textAlign: "center", p: "2%" }}>
        <Typography
          display={"flex"}
          alignItems={"center"}
          justifyContent={"center"}
          paddingBottom={"1rem"}
          borderBottom={`1px solid ${COLORS.divider}`}
          fontWeight={500}
          marginBottom={"1rem"}
        >
          Do you want to Discard Changes?
        </Typography>
        <Typography
          display={"flex"}
          justifyContent={"center"}
          alignItems={"center"}
          textAlign={"center"}
          fontSize={"16px"}
          color={COLORS.textWhite}
          margin={"2rem 0"}
        >
          You have unsaved changes. Are you sure you want to discard them?
        </Typography>
        <Box display={"flex"} alignItems={"center"} justifyContent={"space-between"} gap={2}>
          <Button
            component="label"
            variant="contained"
            sx={{
              color: "#fff",
              border: `1px solid ${COLORS.adminAppPrimaryBrandColor}`,
              backgroundColor: "transparent",
              "&:hover": {
                backgroundColor: `${COLORS.secondaryButton}`,
              },
            }}
            onClick={() => {
              toggle(false)
              onCancel()
            }}
          >
            Discard Changes
          </Button>
          <Button
            component="label"
            variant="contained"
            sx={{
              color: "#fff",
              backgroundColor: `${COLORS.primaryButton}`,
              "&:hover": {
                backgroundColor: `${COLORS.primaryButton}`,
              },
            }}
            onClick={() => {
              toggle(false)
              onConfirm()
            }}
          >
            Save & Close
          </Button>
        </Box>
      </Box>
    </ModalPopup>
  )
}
